// This file contains the utility functions for the sampling event JSA tab.
// Mainly it will be used to handle the Autosave functionality for the JSA tab.
import { autosaveSamplingEvent } from "../../../../api/SamplingEventsApi";

export class JsaTabUtils {
  constructor(samplingEventId, jsaData) {
    this.jsaData = jsaData;
    this.samplingEventId = samplingEventId;
  }

  // Change args to snake_case
  toSnakeCase = (str) => {
    if (str == null) return str;
    return String(str).replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`
    );
  };

  formatDateForApi = (date) => {
    if (!date) return null;
    try {
      const d = new Date(date);
      // Use UTC methods to avoid timezone issues
      const year = d.getUTCFullYear();
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const day = String(d.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error("Error formatting date for API:", e);
      return null;
    }
  };

  // Copy JSA
  // Args: samplingEventId, jsaId, completedDate
  // samplingEventId is the id of the sampling event we need to copy the JSA to.
  // jsaId is the id of the JSA we need to copy.
  // completedDate is the date the JSA was completed.
  // Returns: True if the JSA was copied successfully, false otherwise.
  autosaveCopyJsa = async (completedDate) => {
    const patchData = {
      action: "copy_jsa",
      id: this.jsaData.jsaBasicInfo.id,
      logs: {
        data: {
          // jsa_completed_date: this.formatDateForApi(completedDate),
          jsa_completed_date: completedDate,
        },
      },
    };
    // Send the patch data to the backend
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error(
          "Error copying JSA:",
          response.data,
          "Patch data:",
          patchData
        );
        return false;
      } else {
        console.log("JSA copied successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error copying JSA:", error);
      return false;
    }
  };

  // Delete JSA
  // Args: samplingEventId, jsaId
  // samplingEventId is the id of the sampling event we need to delete the JSA from.
  // jsaId is the id of the JSA we need to delete.
  // Returns: True if the JSA was deleted successfully, false otherwise.
  autosaveDeleteJsa = async () => {
    const patchData = {
      action: "delete_jsa",
      id: this.jsaData.jsaBasicInfo.id,
    };
    // Send the patch data to the backend
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error deleting JSA:", response.data);
        return false;
      } else {
        console.log("JSA deleted successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error deleting JSA:", error);
      return false;
    }
  };

  // Basic Info autosave
  autosaveJsaBasicInfo = async (field, value) => {
    // Put into snake_case
    const fieldSnakeCase = this.toSnakeCase(field);
    const patchData = {
      action: "update_jsa_basic_info",
      id: this.jsaData.jsaBasicInfo.id,
      logs: {
        data: {
          [fieldSnakeCase]: value,
        },
      },
    };
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error updating JSA basic info:", response.data);
        return false;
      } else {
        console.log("JSA basic info updated successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error updating JSA basic info:", error);
      return false;
    }
  };

  // RAC Info autosave
  autosaveJsaRacInfo = async (field, value) => {
    // Put into snake_case
    const fieldSnakeCase = this.toSnakeCase(field);
    const patchData = {
      action: "update_jsa_rac_info",
      id: this.jsaData.jsaBasicInfo.id,
      logs: {
        data: {
          [fieldSnakeCase]: value,
        },
      },
    };
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error updating JSA rac info:", response.data);
        return false;
      } else {
        console.log("JSA rac info updated successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error updating JSA rac info:", error);
      return false;
    }
  };

  // Hazards Info autosave
  autosaveJsaHazards = async (field, value) => {
    // Put into snake_case
    const fieldSnakeCase = this.toSnakeCase(field);
    const patchData = {
      action: "update_jsa_hazards",
      id: this.jsaData.jsaBasicInfo.id,
      logs: {
        data: {
          [fieldSnakeCase]: value,
        },
      },
    };
    console.log("Patch data:", patchData);
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error updating JSA hazards:", response.data);
        return false;
      } else {
        console.log("JSA hazards updated successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error updating JSA hazards:", error);
      return false;
    }
  };

  // Control Measures autosave
  autosaveJsaControlMeasures = async (field, value) => {
    // Put into snake_case
    const fieldSnakeCase = this.toSnakeCase(field);
    const patchData = {
      action: "update_jsa_control_measures",
      id: this.jsaData.jsaBasicInfo.id,
      logs: {
        data: {
          [fieldSnakeCase]: value,
        },
      },
    };
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error updating JSA control measures:", response.data);
        return false;
      } else {
        console.log(
          "JSA control measures updated successfully:",
          response.data
        );
        return true;
      }
    } catch (error) {
      console.error("Error updating JSA control measures:", error);
      return false;
    }
  };

  // Signature Info autosave, this will take a list of logs and apply them to the JSA signature info
  autosaveJsaSignatureInfo = async (logs) => {
    // Checking if the logs is an array
    if (!Array.isArray(logs)) {
      logs = [logs];
    }
    const patchData = {
      action: "update_jsa_signatures",
      id: this.jsaData.jsaBasicInfo.id,
      signature_action: "save_signature",
      logs: {
        data: logs,
      },
    };
    console.log("Patch data:", patchData);
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error updating JSA signature info:", response.data);
        return false;
      } else {
        console.log("JSA signature info updated successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error updating JSA signature info:", error);
      return false;
    }
  };

  // Delete JSA signature
  autosaveDeleteJsaSignature = async (id) => {
    const patchData = {
      action: "update_jsa_signatures",
      id: this.jsaData.jsaBasicInfo.id,
      signature_action: "delete_signature",
      logs: {
        data: {
          id: id,
        },
      },
    };
    try {
      const response = await autosaveSamplingEvent(
        this.samplingEventId,
        patchData
      );
      if (response.status !== 200) {
        console.error("Error deleting JSA signature:", response.data);
        return false;
      } else {
        console.log("JSA signature deleted successfully:", response.data);
        return true;
      }
    } catch (error) {
      console.error("Error deleting JSA signature:", error);
      return false;
    }
  };
}
